import { Link } from 'react-router-dom';
import { ELECTION, IDENTITY } from '@content/identity';
import { Footer } from '@/components/Footer';
import { STORAGE_INVENTORY } from '@/lib/storage-inventory';

/**
 * Art. 13 GDPR notice.
 *
 * The controller identity is pulled from content/identity.ts, and the storage
 * table from the same inventory that docs/cookies.md documents — so the page
 * cannot drift from either the legal entity or what the site actually stores.
 */
export function PrivacyPage() {
  return (
    <div className="flex min-h-full flex-col bg-white">
      <main className="mx-auto w-full max-w-3xl flex-1 px-4 py-8">
        <Link to="/" className="text-sm text-brand-700 underline hover:no-underline">
          ← Zpět na mapu
        </Link>

        <h1 className="mt-4 text-2xl font-bold text-slate-900">Ochrana osobních údajů</h1>
        <p className="mt-2 text-sm text-slate-600">
          Informace podle čl. 13 nařízení (EU) 2016/679 (GDPR).
        </p>

        <Section title="Kdo údaje zpracovává (správce)">
          <p>
            <strong>{IDENTITY.movement}</strong>
            <br />
            IČO: {IDENTITY.ico}
            <br />
            Sídlo: {IDENTITY.address}
            <br />
            Datová schránka: {IDENTITY.dataBox}
            <br />
            E-mail:{' '}
            <a className="underline" href={`mailto:${IDENTITY.contact}`}>
              {IDENTITY.contact}
            </a>
            <br />
            Telefon: {IDENTITY.phone}
          </p>
          <p className="mt-2">
            Web provozujeme pro kandidátku <strong>{IDENTITY.list}</strong> pro {ELECTION.name}{' '}
            ({ELECTION.dates}). Nemáme pověřence pro ochranu osobních údajů — nejsme orgán veřejné
            moci a nezpracováváme osobní údaje ve velkém rozsahu ani systematicky.
          </p>
        </Section>

        <Section title="Co se ukládá ve vašem prohlížeči">
          <p>
            Následující tabulka je úplná. Nic, co v ní není uvedeno, tento web neukládá. Nepoužíváme
            žádnou analytiku, žádné měřicí pixely, žádná vložená videa ani mapy z cizích serverů a
            žádná písma načítaná odjinud. Mapové podklady, písma i ikony hostujeme sami.
          </p>

          <div className="mt-3 overflow-x-auto">
            <table className="w-full border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-slate-300">
                  <th className="py-2 pr-3 font-semibold">Klíč</th>
                  <th className="py-2 pr-3 font-semibold">Typ</th>
                  <th className="py-2 pr-3 font-semibold">Účel</th>
                  <th className="py-2 pr-3 font-semibold">Doba</th>
                  <th className="py-2 font-semibold">Souhlas</th>
                </tr>
              </thead>
              <tbody>
                {STORAGE_INVENTORY.map((row) => (
                  <tr key={row.key} className="border-b border-slate-200 align-top">
                    <td className="py-2 pr-3 font-mono text-xs">{row.key}</td>
                    <td className="py-2 pr-3">{row.type}</td>
                    <td className="py-2 pr-3">{row.purpose}</td>
                    <td className="py-2 pr-3">{row.lifetime}</td>
                    <td className="py-2">{row.consent}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="mt-3">
            Cookie <code className="font-mono text-xs">vote_id</code> se nastavuje{' '}
            <strong>až ve chvíli, kdy poprvé hlasujete nebo odešlete podnět</strong> — nikdy při
            pouhém načtení stránky. Právě proto spadá pod výjimku „nezbytné pro službu výslovně
            vyžádanou uživatelem“ podle § 89 odst. 3 zákona č. 127/2005 Sb. o elektronických
            komunikacích a nevyžaduje souhlas.
          </p>
        </Section>

        <Section title="Účely a právní základy">
          <dl className="space-y-3">
            <Entry term="Zveřejnění podnětu na mapě">
              Odesláním podnětu nám poskytujete jeho text a polohu. Formulář nesbírá jméno, e-mail
              ani telefon — o odesílateli tedy nemáme žádné kontaktní údaje. Právní základ:{' '}
              <strong>oprávněný zájem</strong> (čl. 6 odst. 1 písm. f) na veřejné diskusi o stavu
              města.
            </Entry>
            <Entry term="Zamezení opakovaného hlasování">
              Ukládáme <strong>otisk (hash)</strong> vzniklý z náhodného identifikátoru v cookie a
              vaší IP adresy. Právní základ: <strong>oprávněný zájem</strong> (čl. 6 odst. 1 písm. f)
              na tom, aby výsledek hlasování nešlo triviálně zmanipulovat.
            </Entry>
            <Entry term="Přihlášení administrátora">
              Právní základ: <strong>plnění smlouvy</strong> / oprávněný zájem na zabezpečení
              administrace.
            </Entry>
            <Entry term="Cokoli nad tento rámec">
              Nic takového dnes neprovozujeme. Pokud bychom přidali analytiku nebo marketingové
              nástroje, budeme je používat výhradně na základě{' '}
              <strong>souhlasu</strong> (čl. 6 odst. 1 písm. a), který půjde stejně snadno udělit
              i odvolat.
            </Entry>
          </dl>
        </Section>

        <Section title="IP adresa a proč ji neuchováváme">
          <p>
            Vaši IP adresu <strong>neukládáme</strong> — ani do databáze, ani do logů. Používáme ji
            pouze v okamžiku hlasování jako vstup do kryptografického otisku:
          </p>
          <p className="mt-2 rounded-md bg-slate-50 p-3 font-mono text-xs">
            otisk = HMAC-SHA256(denní sůl, identifikátor z cookie + IP adresa)
          </p>
          <p className="mt-2">
            „Denní sůl“ je náhodné tajemství, které se{' '}
            <strong>každý den mění a staré se maže</strong>. Po výměně soli už nelze z IP adresy
            zpětně dopočítat žádný dříve uložený otisk — a to ani nám. Uložené otisky se tím stávají
            nepropojitelnými s konkrétní osobou.
          </p>
        </Section>

        <Section title="Doby uložení">
          <ul className="list-disc space-y-1 pl-5">
            <li>
              <strong>Zveřejněné podněty:</strong> po dobu provozu mapy, tedy do konce volebního
              období, pro které vznikla.
            </li>
            <li>
              <strong>Zamítnuté podněty:</strong> uchováváme záznam o zamítnutí a jeho důvodu kvůli
              doložitelnosti moderace; text podnětu se nezveřejňuje.
            </li>
            <li>
              <strong>Otisky hlasů:</strong> po dobu provozu mapy. Nepropojitelné s osobou už po
              výměně denní soli, tedy nejpozději následující den.
            </li>
            <li>
              <strong>Denní soli:</strong> dva dny, pak se mažou.
            </li>
            <li>
              <strong>Přihlášení administrátora:</strong> 12 hodin.
            </li>
          </ul>
        </Section>

        <Section title="Zveřejňování podnětů a osobní údaje třetích osob">
          <p>
            Odeslané podněty <strong>zveřejňujeme na mapě po kontrole moderátorem</strong>. Podnět
            proto <strong>nesmí obsahovat osobní údaje o jiných lidech</strong> — jména, adresy,
            registrační značky vozidel, popisy konkrétních osob ani nic, podle čeho by šlo někoho
            identifikovat.
          </p>
          <p className="mt-2">
            Podněty, které takové údaje obsahují, moderátoři zamítají s výslovným důvodem „obsahuje
            osobní údaje třetích osob“. Pravidla jsou veřejná —{' '}
            <Link className="underline" to="/pravidla">
              pravidla pro podněty
            </Link>
            .
          </p>
        </Section>

        <Section title="Komu údaje předáváme">
          <p>
            Nikomu. Web běží na vlastním serveru, mapové podklady i písma hostujeme sami,
            nepoužíváme žádnou externí analytiku ani reklamní síť a nepřenášíme data mimo EU.
          </p>
        </Section>

        <Section title="Vaše práva">
          <p>Máte právo:</p>
          <ul className="mt-1 list-disc space-y-1 pl-5">
            <li>na přístup ke svým údajům (čl. 15)</li>
            <li>na opravu (čl. 16)</li>
            <li>na výmaz (čl. 17)</li>
            <li>na omezení zpracování (čl. 18)</li>
            <li>na přenositelnost (čl. 20)</li>
            <li>
              <strong>vznést námitku proti zpracování na základě oprávněného zájmu</strong> (čl. 21)
            </li>
            <li>odvolat souhlas, pokud jsme jej někdy získali (čl. 7 odst. 3)</li>
          </ul>
          <p className="mt-2">
            Uplatnit je můžete na{' '}
            <a className="underline" href={`mailto:${IDENTITY.contact}`}>
              {IDENTITY.contact}
            </a>{' '}
            nebo datovou schránkou {IDENTITY.dataBox}. Upozorňujeme, že u anonymních podnětů a
            hlasů nemusíme být schopni vás k údajům přiřadit — právě proto, že o vás žádné
            identifikační údaje nedržíme (čl. 11 GDPR).
          </p>
        </Section>

        <Section title="Stížnost k dozorovému úřadu">
          <p>
            Máte právo podat stížnost u Úřadu pro ochranu osobních údajů, Pplk. Sochora 27, 170 00
            Praha 7,{' '}
            <a className="underline" href="https://uoou.gov.cz" rel="noopener">
              uoou.gov.cz
            </a>
            .
          </p>
        </Section>
      </main>

      <Footer />
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-6">
      <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
      <div className="mt-2 space-y-2 text-slate-700">{children}</div>
    </section>
  );
}

function Entry({ term, children }: { term: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="font-medium text-slate-900">{term}</dt>
      <dd className="text-slate-700">{children}</dd>
    </div>
  );
}
